import { Button } from "../../../components/ui/button";
import { useState, type RefObject } from "react";
import { formatDateTime } from "../../utils/date";
import { Stethoscope, MessageSquare, Plus, ChevronRight, History, User2 } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientDetailsLabels } from "../labels";
import type { TimelineEvent } from "../../../types";
import { cn } from "../../../lib/utils";
import { NoteModal } from "./NoteModal";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";

interface Props {
  history: any[];
  timelineEvents: TimelineEvent[];
  clientId: number | string;
  loading: boolean;
  hasMore: boolean;
  loaderRef: RefObject<HTMLDivElement>;
  onNeedEvolution: (h: any) => void;
  onTimelineRefresh?: (newEvent?: TimelineEvent) => void;
}

export function HistorySection({
  history,
  timelineEvents,
  clientId,
  loading,
  hasMore,
  loaderRef,
  onNeedEvolution,
  onTimelineRefresh,
}: Props) {
  const { lang } = useLanguage();
  const hT = clientDetailsLabels[lang].historySection;
  const tT = clientDetailsLabels[lang].timelineSection;
  
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const historyArr = Array.isArray(history) ? history : [];
  const timelineArr = Array.isArray(timelineEvents) ? timelineEvents : [];
  
  const pastHistory = historyArr.filter(h => new Date(h.start_datetime) < new Date() || h.done);

  const parseDate = (d: any) => {
    if (!d) return new Date(0);
    const dateStr = typeof d === 'string' ? d.replace(' ', 'T') : d;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? new Date(0) : date;
  };

  const combinedHistory = [
    ...pastHistory.map(h => ({
      ...h,
      itemType: 'appointment' as const,
      sortDate: parseDate(h.start_datetime)
    })),
    ...timelineArr.map(e => ({
      ...e,
      itemType: 'timeline' as const,
      sortDate: parseDate(e.created_at)
    }))
  ].sort((a, b) => {
    const dateDiff = b.sortDate.getTime() - a.sortDate.getTime();
    if (dateDiff !== 0) return dateDiff;
    return (b.id || 0) - (a.id || 0);
  });

  const getEventDate = (event: any) => {
    return event.itemType === 'appointment' ? event.start_datetime : event.created_at;
  };

  const renderEvent = (event: any, index: number, total: number) => {
    const isAppointment = event.itemType === 'appointment';
    const Icon = isAppointment ? Stethoscope : MessageSquare;
    const isPending = isAppointment && !event.done && !event.evolution;

    return (
      <div key={`${event.itemType}-${event.id}`} className="relative pl-8 pb-8 group last:pb-0">
        {/* Vertical Line */}
        {index < total - 1 && (
          <div className="absolute left-[11px] top-7 bottom-0 w-[2px] bg-neutral-100 group-hover:bg-primary/20 transition-colors" />
        )}
        
        {/* Dot Icon */}
        <div className={cn(
          "absolute left-0 top-1 h-6 w-6 rounded-xl border-2 flex items-center justify-center bg-white shadow-sm z-10 transition-transform group-hover:scale-110",
          isAppointment ? "border-blue-100 text-blue-500" : "border-neutral-100 text-neutral-400",
          isPending && "border-amber-100 text-amber-500"
        )}>
          <Icon className="h-3 w-3" />
        </div>

        <div className="flex flex-col gap-2">
          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className={cn(
                "text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full",
                isAppointment ? "bg-blue-50 text-blue-600" : "bg-neutral-50 text-neutral-500",
                isPending && "bg-amber-50 text-amber-600"
              )}>
                {isAppointment ? hT.title : tT.types.note}
              </span>
              {!isAppointment && event.author_name && (
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-neutral-50 rounded-full border border-neutral-100/50">
                  <User2 className="w-2.5 h-2.5 text-neutral-400" />
                  <span className="text-[9px] text-neutral-500 font-bold uppercase tracking-tight">
                    {event.author_name}
                  </span>
                </div>
              )}
            </div>
            <span className="text-[10px] font-bold text-neutral-400 tabular-nums uppercase tracking-tighter">
              {formatDateTime(getEventDate(event))}
            </span>
          </div>

          {/* Content Card */}
          <div className={cn(
            "p-4 rounded-2xl border transition-all",
            isAppointment 
              ? "bg-blue-50/[0.15] border-blue-50 text-neutral-700" 
              : "bg-white border-neutral-100 text-neutral-600"
          )}>
            {isAppointment ? (
              <div className="flex flex-col gap-2">
                {event.evolution ? (
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{event.evolution}</div>
                ) : isPending ? (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-amber-600 uppercase tracking-widest opacity-80">
                      {hT.pendingEvolution}
                    </span>
                    <Button 
                      variant="ghost"
                      size="sm"
                      className="h-7 px-3 text-[10px] font-bold uppercase tracking-widest text-amber-600 hover:bg-amber-50 rounded-full border border-amber-100"
                      onClick={() => onNeedEvolution(event)}
                    >
                      {hT.fillEvolution}
                    </Button>
                  </div>
                ) : (
                  <div className="italic text-neutral-400 text-xs font-medium">{hT.empty}</div>
                )}
              </div>
            ) : (
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{event.content}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="p-6 border-b border-neutral-50 flex items-center justify-between bg-gradient-to-r from-white to-neutral-50/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <History className="w-4 h-4" />
          </div>
          <span className="font-bold text-neutral-800 tracking-tight">{tT.title}</span>
        </div>
        <Button 
          onClick={() => setIsNoteModalOpen(true)}
          variant="outline"
          size="sm"
          className="h-9 px-4 gap-2 text-[10px] font-bold uppercase tracking-widest border-neutral-100 hover:bg-neutral-50 rounded-xl"
        >
          <Plus className="h-3.5 w-3.5" />
          {tT.postButton}
        </Button>
      </div>

      <NoteModal 
        clientId={clientId}
        isOpen={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        onSuccess={onTimelineRefresh}
      />

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 border-l-0">
          <SheetHeader className="p-8 border-b bg-neutral-50/50">
            <SheetTitle className="text-lg font-bold uppercase tracking-widest text-neutral-800 flex items-center gap-3">
              <History className="w-5 h-5 text-primary" />
              {tT.title}
            </SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto h-[calc(100vh-100px)] p-8">
             <div className="relative">
               {combinedHistory.map((ev, i) => renderEvent(ev, i, combinedHistory.length))}
               
               {loading && (
                 <div className="text-center py-8 text-[10px] font-bold text-neutral-300 uppercase tracking-[0.2em] animate-pulse">
                   {hT.loading}
                 </div>
               )}
               <div ref={loaderRef} className="h-8" />
             </div>
          </div>
        </SheetContent>
      </Sheet>
      
      <div className="flex-1 p-6">
        <div className="relative">
          {combinedHistory.slice(0, 5).map((ev, i) => renderEvent(ev, i, Math.min(5, combinedHistory.length)))}
          
          {combinedHistory.length > 5 && (
            <div className="flex justify-center pt-6">
              <Button 
                onClick={() => setIsSheetOpen(true)}
                variant="ghost"
                size="sm"
                className="h-10 px-6 gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400 hover:text-primary hover:bg-primary/5 rounded-2xl transition-all"
              >
                Ver histórico completo
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {combinedHistory.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 rounded-2xl bg-neutral-50 flex items-center justify-center text-neutral-300 mb-4">
                <History className="w-6 h-6" />
              </div>
              <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">
                {tT.empty}
              </p>
            </div>
          )}

          {loading && combinedHistory.length === 0 && (
            <div className="flex flex-col gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-neutral-50 rounded-2xl animate-pulse" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
